import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconButton, ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material';
import {
  MouseEvent,
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { faBan } from '@fortawesome/pro-solid-svg-icons/faBan';
import { faCalendarAlt } from '@fortawesome/pro-solid-svg-icons/faCalendarAlt';
import { faClock } from '@fortawesome/pro-solid-svg-icons/faClock';
import { faEllipsisV } from '@fortawesome/pro-solid-svg-icons/faEllipsisV';
import { faICursor } from '@fortawesome/pro-solid-svg-icons/faICursor';
import { faShareNodes } from '@fortawesome/pro-solid-svg-icons/faShareNodes';
import { useTranslation } from 'react-i18next';
import { CancelScheduleDialog } from './CancelScheduleDialog';
import { PublishTemplateVersionDialog } from './PublishTemplateVersionDialog';
import { RenameTemplateVersionDialog } from './RenameTemplateVersionDialog';
import { ReplaceScheduledVersionDialog } from './ReplaceScheduledVersionDialog';
import { SchedulePublicationDialog } from './SchedulePublicationDialog';
import { useEditorStore } from '../../store';
import { useProgram } from '../../../loyalty/ProgramContext';
import { useScheduledTemplate, useTemplates } from '../../hooks/use-templates';

export interface TemplateVersionMenuContextValue {
  openMenu: (event: MouseEvent<HTMLElement>, templateId: string) => void;
}

export const TemplateVersionMenuContext = createContext<TemplateVersionMenuContextValue | null>(
  null,
);

export function TemplateVersionMenuManager({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const { program } = useProgram();

  const currentTemplateId = useEditorStore(state => state.currentTemplateId);

  const { templates } = useTemplates();
  const { data: scheduledTemplate } = useScheduledTemplate();
  const [menuAnchor, setMenuAnchor] = useState<{ element: HTMLElement; templateId: string } | null>(
    null,
  );

  const isPublished = useMemo(
    () => menuAnchor?.templateId === program?.templateId,
    [menuAnchor?.templateId, program?.templateId],
  );

  const isScheduled = useMemo(
    () => menuAnchor?.templateId === scheduledTemplate?.id,
    [menuAnchor?.templateId, scheduledTemplate?.id],
  );

  const [nameVersionDialog, setNameVersionDialog] = useState<{
    open: boolean;
    templateId: string;
    currentName?: string;
    currentDescription?: string;
  }>({ open: false, templateId: currentTemplateId || '' });

  const [publishDialog, setPublishDialog] = useState<{
    open: boolean;
    templateId: string;
    templateName?: string;
  }>({ open: false, templateId: '' });

  const [scheduleDialog, setScheduleDialog] = useState<{
    open: boolean;
    templateId: string;
    existingPublicationDate?: Date | null;
  }>({ open: false, templateId: '' });

  const [replaceScheduleDialog, setReplaceScheduleDialog] = useState<{
    open: boolean;
    newTemplateId: string;
    newPublicationDate: Date;
  }>({ open: false, newTemplateId: '', newPublicationDate: new Date() });

  const [cancelScheduleDialog, setCancelScheduleDialog] = useState<{
    open: boolean;
    templateName?: string;
  }>({ open: false });

  const handleMenuOpen = useCallback((event: MouseEvent<HTMLElement>, templateId: string) => {
    event.stopPropagation();
    setMenuAnchor({ element: event.currentTarget, templateId });
  }, []);

  const contextValue: TemplateVersionMenuContextValue = useMemo(
    () => ({
      openMenu: handleMenuOpen,
    }),
    [handleMenuOpen],
  );

  const handleMenuClose = useCallback(() => {
    setMenuAnchor(null);
  }, []);

  const openRenameDialog = useCallback(
    (templateId: string) => {
      const templateVersion = templates.find(t => t.id === templateId);
      setNameVersionDialog({
        open: true,
        templateId,
        currentName: templateVersion?.name || undefined,
        currentDescription: templateVersion?.description || undefined,
      });
      handleMenuClose();
    },
    [templates, handleMenuClose],
  );

  const openPublishDialog = useCallback(
    (templateId: string) => {
      const templateVersion = templates.find(t => t.id === templateId);
      setPublishDialog({
        open: true,
        templateId,
        templateName: templateVersion?.name || undefined,
      });
      handleMenuClose();
    },
    [templates, handleMenuClose],
  );

  const handleClosePublishDialog = useCallback(() => {
    setPublishDialog({ open: false, templateId: '' });
  }, []);

  const handleCloseRenameDialog = useCallback(() => {
    setNameVersionDialog({ open: false, templateId: '' });
  }, []);

  const openScheduleDialog = useCallback(
    (templateId: string, isEdit = false) => {
      const existingPublicationDate =
        isEdit && scheduledTemplate?.scheduledPublicationDate
          ? new Date(scheduledTemplate.scheduledPublicationDate)
          : undefined;
      setScheduleDialog({
        open: true,
        templateId,
        existingPublicationDate,
      });
      handleMenuClose();
    },
    [scheduledTemplate?.scheduledPublicationDate, handleMenuClose],
  );

  const handleCloseScheduleDialog = useCallback(() => {
    setScheduleDialog({ open: false, templateId: '' });
  }, []);

  const handleOpenReplaceDialog = useCallback((newTemplateId: string, newPublicationDate: Date) => {
    setReplaceScheduleDialog({
      open: true,
      newTemplateId,
      newPublicationDate,
    });
  }, []);

  const handleCloseReplaceScheduleDialog = useCallback(() => {
    setReplaceScheduleDialog({ open: false, newTemplateId: '', newPublicationDate: new Date() });
    handleCloseScheduleDialog();
  }, [handleCloseScheduleDialog]);

  const openCancelScheduleDialog = useCallback(() => {
    setCancelScheduleDialog({
      open: true,
      templateName: scheduledTemplate?.name || undefined,
    });
    handleMenuClose();
  }, [scheduledTemplate?.name, handleMenuClose]);

  const handleCloseCancelScheduleDialog = useCallback(() => {
    setCancelScheduleDialog({ open: false });
  }, []);

  return (
    <TemplateVersionMenuContext.Provider value={contextValue}>
      {children}
      <Menu
        anchorEl={menuAnchor?.element}
        open={!!menuAnchor}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={() => menuAnchor && openRenameDialog(menuAnchor.templateId)}>
          <ListItemIcon>
            <FontAwesomeIcon icon={faICursor} size="sm" />
          </ListItemIcon>
          <ListItemText>{t('editor.template.version.name.action')}</ListItemText>
        </MenuItem>
        {!isPublished && (
          <MenuItem onClick={() => menuAnchor && openPublishDialog(menuAnchor.templateId)}>
            <ListItemIcon>
              <FontAwesomeIcon icon={faShareNodes} size="sm" />
            </ListItemIcon>
            <ListItemText>{t('editor.template.version.publish.now')}</ListItemText>
          </MenuItem>
        )}
        {!isPublished && !isScheduled && (
          <MenuItem onClick={() => menuAnchor && openScheduleDialog(menuAnchor.templateId)}>
            <ListItemIcon>
              <FontAwesomeIcon icon={faClock} size="sm" />
            </ListItemIcon>
            <ListItemText>{t('editor.template.version.schedule.action')}</ListItemText>
          </MenuItem>
        )}
        {isScheduled && (
          <MenuItem onClick={() => menuAnchor && openScheduleDialog(menuAnchor.templateId, true)}>
            <ListItemIcon>
              <FontAwesomeIcon icon={faCalendarAlt} size="sm" />
            </ListItemIcon>
            <ListItemText>{t('editor.template.version.schedule.edit')}</ListItemText>
          </MenuItem>
        )}
        {isScheduled && (
          <MenuItem onClick={openCancelScheduleDialog}>
            <ListItemIcon>
              <FontAwesomeIcon icon={faBan} size="sm" />
            </ListItemIcon>
            <ListItemText>{t('editor.template.version.schedule.cancel')}</ListItemText>
          </MenuItem>
        )}
      </Menu>
      <RenameTemplateVersionDialog
        open={nameVersionDialog.open}
        templateId={nameVersionDialog.templateId}
        currentName={nameVersionDialog.currentName}
        currentDescription={nameVersionDialog.currentDescription}
        onClose={handleCloseRenameDialog}
      />
      <PublishTemplateVersionDialog
        open={publishDialog.open}
        templateId={publishDialog.templateId}
        templateName={publishDialog.templateName}
        onClose={handleClosePublishDialog}
      />
      <SchedulePublicationDialog
        open={scheduleDialog.open}
        templateId={scheduleDialog.templateId}
        existingPublicationDate={scheduleDialog.existingPublicationDate || undefined}
        onClose={handleCloseScheduleDialog}
        onConfirmWithExisting={handleOpenReplaceDialog}
      />
      <ReplaceScheduledVersionDialog
        open={replaceScheduleDialog.open}
        existingScheduledVersion={scheduledTemplate || null}
        newTemplateId={replaceScheduleDialog.newTemplateId}
        newPublicationDate={replaceScheduleDialog.newPublicationDate}
        onClose={handleCloseReplaceScheduleDialog}
        onConfirm={handleCloseScheduleDialog}
      />
      <CancelScheduleDialog
        open={cancelScheduleDialog.open}
        templateName={cancelScheduleDialog.templateName}
        onClose={handleCloseCancelScheduleDialog}
      />
    </TemplateVersionMenuContext.Provider>
  );
}

export interface TemplateVersionMenuButtonProps {
  templateId: string;
  className?: string;
}

export function TemplateVersionMenuButton({
  templateId,
  className,
}: TemplateVersionMenuButtonProps) {
  const context = useContext(TemplateVersionMenuContext);

  if (!context) {
    return null;
  }

  return (
    <IconButton
      className={className}
      onClick={event => context.openMenu(event, templateId)}
      size="small"
    >
      <FontAwesomeIcon icon={faEllipsisV} />
    </IconButton>
  );
}
